import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { LineOfBusiness } from 'src/app/shared/models/enums/line-of-business';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { buildHttpParams } from 'src/app/shared/utilities/interpolate-params';
import { combine } from 'src/app/shared/utilities/route';
import { CategoryLineOfBusiness } from '../enums/category-line-of-business.enum';
import { ClientPortalVisibilitySettings } from 'src/app/compliance/models/client-portal-visibility-settings.model';
import { ClientPortalVisibilityUpdate } from 'src/app/compliance/models/client-portal-visibility-update.model';
import { ComplianceFacilityBulkEdit } from 'src/app/compliance/models/compliance-facility-bulk-edit';
import { ComplianceFacility } from 'src/app/compliance/models/compliance-facility.model';
import { DocDueDate } from 'src/app/compliance/models/doc-due-date.model';
import {
    FacilityAutoMatchSettingsDto,
    FacilityAutoMatchSettingsViewModel
} from '../models/facility-auto-match-settings-dto.model';
import { FacilityDocDueDates } from '../models/facility-doc-due-dates.model';
import { ProfileRequirement } from '../models/profile-requirements.model';

/**
 * @deprecated Do not use this service.  Use service(s) from "src/app/compliance/api" or "src/app/compliance/api/migration"
 */
@Injectable({ providedIn: 'root' })
export class ComplianceFacilitiesService {
    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) readonly settings: Settings,
        private readonly _ldManager: LDFeatureManager
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova', 'compliance', 'facilities');
    }

    // ** FacilitiesController.GetFacilities
    getFacilities(args: FetchArgs): Observable<PagingToken<ComplianceFacility[]>> {
        const url = `${this.coreApiPath}`;
        const params = {};
        buildHttpParams(params, args, '');
        return this.http.get<PagingToken<ComplianceFacility[]>>(url, { params });
    }

    // ** FacilitiesController.GetComplianceFacility
    getFacility(id: number): Observable<ComplianceFacility> {
        return this.http.get<ComplianceFacility>(`${this.coreApiPath}/${id}`);
    }

    // ** FacilitiesController.GetFacilityRequirements
    getFacilityRequirements(id: number, lob: LineOfBusiness, args: FetchArgs): Observable<ProfileRequirement[]> {
        const adjustedLob = [LineOfBusiness.Locums, LineOfBusiness.AccessControl].some((x) => x === lob)
            ? lob
            : LineOfBusiness.Travel;
        const url = `${this.coreApiPath}/${id}/requirements/${this.mapToCategoryLineOfBusiness(adjustedLob)}`;
        const params = {};
        buildHttpParams(params, args, '');
        return this.http.get<ProfileRequirement[]>(url, { params });
    }

    private mapToCategoryLineOfBusiness(lob: LineOfBusiness): CategoryLineOfBusiness {
        switch (lob) {
            case LineOfBusiness.Travel:
                return CategoryLineOfBusiness.Travel;
            case LineOfBusiness.Locums:
                return CategoryLineOfBusiness.Locums;
            case LineOfBusiness.AccessControl:
                return CategoryLineOfBusiness.AccessControl;
            default:
                return null;
        }
    }

    // ** FacilitiesController.GetClientPortalNetworkVisibility
    getClientPortalVisibility(id: number): Observable<ClientPortalVisibilitySettings> {
        return this.http.get<ClientPortalVisibilitySettings>(`${this.coreApiPath}/${id}/client-portal-visibility`);
    }

    // ** FacilitiesController.UpdateRequirementClientPortalVisibility
    updateClientPortalVisibility(id: number, updates: ClientPortalVisibilityUpdate[]): Observable<unknown> {
        return this.http.put(`${this.coreApiPath}/${id}/client-portal-visibility`, updates);
    }

    // ** FacilitiesController.CopyRequirements
    copyRequirements(copyFrom: number, copyTo: number[]): Observable<unknown> {
        return this.http.post(`${this.coreApiPath}/${copyFrom}/copy-requirements`, copyTo);
    }

    // ** FacilitiesController.SearchFacilities
    searchFacilities(name: string): Observable<KeyValue<number, string>[]> {
        return this.http.get<KeyValue<number, string>[]>(`${this.coreApiPath}/search`, {
            params: {
                name
            }
        });
    }

    // ** FacilitiesController.GetDocDueDate
    getDocDueDate(facilityId: number): Observable<FacilityDocDueDates> {
        return this.http.get<FacilityDocDueDates>(`${this.coreApiPath}/${facilityId}/doc-due-date`);
    }

    // ** FacilitiesController.UpdateDocDueDate
    updateDocDueDate(facilityId: number, docDueDate: DocDueDate): Observable<unknown> {
        return this.http.put(`${this.coreApiPath}/${facilityId}/doc-due-date`, docDueDate);
    }

    // ** FacilitiesController.UpdateCompleteStatus
    updateFacilityCompleteStatus(facility: ComplianceFacility): Observable<unknown> {
        return this.http.put(`${this.coreApiPath}/${facility.id}/complete-status`, facility);
    }

    // ** FacilitiesController.UpdateBulkCompleteStatus
    updateBulkFacilityCompleteStatus(bulkEdit: ComplianceFacilityBulkEdit): Observable<unknown> {
        return this.http.put(`${this.coreApiPath}/bulk-complete-status`, bulkEdit);
    }

    // ** FacilitiesController.GetAutoMatchSettingsAsync
    getAutoMatchSettings(facilityId: number): Observable<FacilityAutoMatchSettingsViewModel> {
        return this.http.get<FacilityAutoMatchSettingsViewModel>(
            `${this.coreApiPath}/${facilityId}/automatch-settings`
        );
    }

    // ** FacilitiesController.updateAutoMatchSettingsAsync
    updateAutoMatchSettings(settings: FacilityAutoMatchSettingsDto): Observable<void> {
        return this.http.put<void>(`${this.coreApiPath}/automatch-settings`, settings);
    }

    // ** FacilitiesController.CheckLabRequestConfigurationExistsForFacilitiesAsync
    checkLabRequestConfigurationExistsForFacilities(facilityIds: number[]): Observable<boolean> {
        return this.http.post<boolean>(`${this.coreApiPath}/check-configurations-facilities`, facilityIds);
    }
}
